import { Button, flash, Modal, SubmitButton } from "@xola/ui-kit";
import { useMemoizedFn, useUnmount } from "ahooks";
import clsx from "clsx";
import { omit } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { Purchase } from "types";
import {
    getFulfillmentOperationErrorsByLineItemId,
    getInitialVariantFulfillMap,
    getUpdateFulfillmentPayload,
    UpdateFulfillmentPayload,
} from "./merchandiseFulfillmentHelper";
import { MerchandiseFulfillmentTable } from "./MerchandiseFulfillmentTable";
import { ReportDamagedMerchandiseModal } from "./ReportDamagedMerchandiseModal";
import { api } from "@/api/api";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { refetchMerchandiseCatalogItems } from "@/components/PurchaseCreate/helpers/PurchaseCreateHelpers";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useModal } from "@/hooks/useVisibility";
import { isInventoryTracked, MERCHANDISE_CACHE_KEY } from "@/models/Merchandise";
import { getMerchandisePurchaseItems } from "@/models/MerchandisePurchaseItem";

interface UpdateFulfilledQuantityModalProps {
    purchase: Purchase;
    isOpen: boolean;
    onClose: (newPurchase?: Purchase) => void;
}

export const UpdateFulfilledQuantityModal = ({ purchase, isOpen, onClose }: UpdateFulfilledQuantityModalProps) => {
    const latestPurchaseRef = useRef(purchase);

    useEffect(() => {
        latestPurchaseRef.current = purchase;
    }, [purchase]);

    const handlePurchaseChange = useMemoizedFn((nextPurchase: Purchase) => {
        latestPurchaseRef.current = nextPurchase;
    });

    const handleModalClose = () => {
        onClose(latestPurchaseRef.current);
    };

    return (
        <Modal isOpen={isOpen} size="large" onClose={handleModalClose}>
            <UpdateFulfilledQuantityModalContent
                purchase={purchase}
                onClose={onClose}
                onPurchaseChange={handlePurchaseChange}
            />
        </Modal>
    );
};

const UpdateFulfilledQuantityModalContent = ({
    purchase: initialPurchase,
    onClose,
    onPurchaseChange,
}: Omit<UpdateFulfilledQuantityModalProps, "isOpen"> & { onPurchaseChange: (purchase: Purchase) => void }) => {
    const invalidate = useInvalidate();
    const successLineItemTimerRef = useRef<ReturnType<typeof setTimeout>>();
    const lastPurchaseIdRef = useRef(initialPurchase.id);
    const [purchase, setPurchase] = useState(initialPurchase);
    const [toFulfillMap, setToFulfillMap] = useState(() => getInitialVariantFulfillMap(initialPurchase));
    const [operationErrorsByLineItemId, setOperationErrorsByLineItemId] = useState<Record<string, string[]>>({});
    const [successLineItemIds, setSuccessLineItemIds] = useState<string[]>([]);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const reportDamageModal = useModal();
    const queryClient = useQueryClient();
    const showReportDamage = getMerchandisePurchaseItems(purchase).some((item) => {
        const product = queryClient.getQueryData<{ warePreference?: { inventoryPolicy?: string } }>([
            MERCHANDISE_CACHE_KEY,
            item.product.id,
        ]);
        return product ? isInventoryTracked(product as any) : false;
    });

    useEffect(() => {
        const isPurchaseSwitch = lastPurchaseIdRef.current !== initialPurchase.id;
        lastPurchaseIdRef.current = initialPurchase.id;

        setPurchase(initialPurchase);
        onPurchaseChange(initialPurchase);

        if (isPurchaseSwitch) {
            setToFulfillMap(getInitialVariantFulfillMap(initialPurchase));
            setOperationErrorsByLineItemId({});
            setSuccessLineItemIds([]);
            clearTimeout(successLineItemTimerRef.current);
        }
    }, [initialPurchase, onPurchaseChange]);

    const { mutateAsync: updateFulfillment, isLoading } = useMutation(async (payload: UpdateFulfillmentPayload) => {
        return api.patch<Purchase>(`/purchases/${purchase.id}`, payload, {
            suppressErrorToast: [400, 409],
        });
    });

    const handleSubmit = async () => {
        const payload = getUpdateFulfillmentPayload(purchase, toFulfillMap);
        if (!payload) {
            return;
        }

        try {
            setOperationErrorsByLineItemId({});
            setSuccessLineItemIds([]);
            clearTimeout(successLineItemTimerRef.current);
            const { data: newPurchase } = await updateFulfillment(payload);

            setPurchase(newPurchase);
            setToFulfillMap(getInitialVariantFulfillMap(newPurchase));
            onPurchaseChange(newPurchase);

            flash.success("Fulfillment updated successfully");
            invalidate("purchases", newPurchase.id);

            const merchandiseProductIds = [
                ...new Set(getMerchandisePurchaseItems(newPurchase).map((item) => item.product.id)),
            ];
            for (const productId of merchandiseProductIds) {
                void refetchMerchandiseCatalogItems(productId);
            }

            onClose(newPurchase);
        } catch (error: any) {
            const {
                errorByLineItemId,
                hasFulfillmentOperationFailure,
                successLineItemIds: parsedSuccessLineItemIds,
            } = getFulfillmentOperationErrorsByLineItemId(error);

            if (hasFulfillmentOperationFailure) {
                setOperationErrorsByLineItemId(errorByLineItemId);
            }

            if (parsedSuccessLineItemIds.length > 0) {
                setSuccessLineItemIds(parsedSuccessLineItemIds);
                clearTimeout(successLineItemTimerRef.current);
                successLineItemTimerRef.current = setTimeout(() => {
                    setSuccessLineItemIds([]);
                }, 3000);
            }

            if (hasFulfillmentOperationFailure || parsedSuccessLineItemIds.length > 0) {
                setIsRefreshing(true);
                try {
                    const { data: refreshedPurchase } = await api.get<Purchase>(`/purchases/${purchase.id}`, {
                        suppressErrorToast: true,
                    });

                    setPurchase(refreshedPurchase);
                    const freshFulfillMap = getInitialVariantFulfillMap(refreshedPurchase);
                    if (hasFulfillmentOperationFailure) {
                        for (const lineItemId of Object.keys(errorByLineItemId)) {
                            if (toFulfillMap[lineItemId] !== undefined) {
                                freshFulfillMap[lineItemId] = toFulfillMap[lineItemId];
                            }
                        }
                    }
                    setToFulfillMap(freshFulfillMap);
                    onPurchaseChange(refreshedPurchase);
                    invalidate("purchases", refreshedPurchase.id);
                } catch {
                    // Keep current local state when refresh fails.
                } finally {
                    setIsRefreshing(false);
                }
            }

            flash.danger("Failed to update fulfillment");
        }
    };

    useUnmount(() => {
        clearTimeout(successLineItemTimerRef.current);
    });

    const handleClose = () => {
        onClose(purchase);
    };

    const handleToFulfillChange = (lineItemId: string, value: number) => {
        if (operationErrorsByLineItemId[lineItemId]) {
            setOperationErrorsByLineItemId((previous) => {
                return omit(previous, [lineItemId]);
            });
        }

        setToFulfillMap((previous) => ({ ...previous, [lineItemId]: value }));
    };

    const hasChanges = Object.values(toFulfillMap).some((value) => value > 0);

    return (
        <>
            <Modal.Header onClose={handleClose}>Update Fulfilled Quantities</Modal.Header>
            <Modal.Body>
                <MerchandiseFulfillmentTable
                    purchase={purchase}
                    toFulfillMap={toFulfillMap}
                    errorByLineItemId={operationErrorsByLineItemId}
                    successLineItemIds={successLineItemIds}
                    onToFulfillChange={handleToFulfillChange}
                />
            </Modal.Body>
            <Modal.Footer className={clsx("flex", showReportDamage ? "justify-between" : "justify-end")}>
                {showReportDamage && (
                    <Button color="danger" onClick={reportDamageModal.show}>
                        Report Damage
                    </Button>
                )}

                <div className="flex space-x-2">
                    <SecondaryButton onClick={handleClose}>Cancel</SecondaryButton>
                    <SubmitButton
                        isLoading={isLoading || isRefreshing}
                        disabled={!hasChanges || isLoading || isRefreshing}
                        onClick={handleSubmit}
                    >
                        Apply
                    </SubmitButton>
                </div>
            </Modal.Footer>
            <ReportDamagedMerchandiseModal
                purchase={purchase}
                isOpen={reportDamageModal.isVisible}
                onClose={reportDamageModal.hide}
            />
        </>
    );
};
